import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import PasswordInputWithEye from "@/components/PasswordInputWithEye";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function SignupPage() {
  const { signup, completeAuth } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", company_name: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await signup(form);
      if (result.token) {
        // OTP disabled: direct signup completion
        completeAuth(result.token, result.user);
        toast.success("Compte créé — bienvenue sur AfriMeet !");
        navigate("/app/dashboard");
      } else {
        toast.success("Un code de vérification vient d'être envoyé à votre adresse email.");
        navigate("/auth/verify", { state: { email: result.email, purpose: result.purpose } });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Création échouée");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-af grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12 bg-af-hero border-r border-af">
        <Link to="/" className="flex items-center gap-3">
          <Logo size={52} />
          <span className="font-display font-extrabold text-2xl">AfriMeet</span>
        </Link>
        <div>
          <h2 className="font-display font-extrabold text-4xl leading-tight mb-4">
            Commencez à automatiser vos comptes-rendus aujourd'hui.
          </h2>
          <p className="text-af-secondary leading-relaxed max-w-md">
            Inscription gratuite. Aucune carte requise. Première réunion résumée en moins de 2 minutes.
          </p>
        </div>
        <div className="text-xs text-af-muted">© 2026 AfriMeet</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h1 className="font-display font-extrabold text-3xl mb-2">Créer un compte</h1>
          <p className="text-af-secondary mb-8">Démarrez votre espace AfriMeet</p>
          <form onSubmit={onSubmit} className="space-y-5">
            <div>
              <Label htmlFor="full_name">Nom complet</Label>
              <Input id="full_name" required data-testid="signup-name-input"
                value={form.full_name} onChange={set("full_name")}
                className="mt-1.5 border-af focus-visible:ring-terracotta" placeholder="Aminata Diallo" />
            </div>
            <div>
              <Label htmlFor="company">Entreprise (optionnel)</Label>
              <Input id="company" data-testid="signup-company-input"
                value={form.company_name} onChange={set("company_name")}
                className="mt-1.5 border-af focus-visible:ring-terracotta" placeholder="Ma société" />
            </div>
            <div>
              <Label htmlFor="email">Email professionnel</Label>
              <Input id="email" type="email" required data-testid="signup-email-input"
                value={form.email} onChange={set("email")}
                className="mt-1.5 border-af focus-visible:ring-terracotta" placeholder="vous@entreprise.com" />
            </div>
            <div>
              <Label htmlFor="password">Mot de passe (min. 6 caractères)</Label>
              <PasswordInputWithEye
                id="password"
                required
                minLength={6}
                dataTestId="signup-password-input"
                value={form.password}
                onChange={set("password")}
                placeholder="••••••••"
              />
            </div>
            <Button type="submit" disabled={loading} data-testid="signup-submit-btn"
              className="w-full bg-terracotta hover:bg-[#9C7124] text-white h-11 rounded-md">
              {loading ? "Création…" : "Créer mon compte"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
          <div className="mt-6 text-sm text-af-secondary text-center">
            Déjà inscrit ?{" "}
            <Link to="/auth/login" className="text-terracotta font-semibold hover:underline" data-testid="goto-login-link">
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
